/**
 * Word 导出模板选择对话框
 * 点击"导出 Word"后弹出，提供三种目录编号模板供选择
 */
import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { ExportTemplate, documentApi } from '../services/api';

interface WordExportDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (templateId: string) => void;
}

export default function WordExportDialog({ open, onClose, onConfirm }: WordExportDialogProps) {
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string>('structure_only_num');
  const [loading, setLoading] = useState(false);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  useEffect(() => {
    if (open) {
      setLoading(true);
      documentApi.getExportTemplates()
        .then((res) => {
          const list = res.data.templates || [];
          setTemplates(list);
          if (list.length > 0 && !list.find(t => t.id === selectedIdRef.current)) {
            setSelectedId(list[0].id);
          }
        })
        .catch(() => {
          // 降级使用默认模板
        })
        .finally(() => setLoading(false));
    }
    // selectedId 通过 ref 读取，无需作为依赖（仅在模板列表加载时校验当前选中项是否存在）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const selected = templates.find(t => t.id === selectedId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* 对话框 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">选择目录编号模板</h2>
            <p className="text-sm text-gray-500 mt-0.5">请选择一种目录编号格式用于 Word 导出</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 模板列表 */}
        <div className="px-6 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
              <span className="ml-3 text-gray-500 text-sm">加载模板...</span>
            </div>
          ) : (
            templates.map((tpl) => (
              <label
                key={tpl.id}
                className={`
                  flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${selectedId === tpl.id
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="exportTemplate"
                  value={tpl.id}
                  checked={selectedId === tpl.id}
                  onChange={() => setSelectedId(tpl.id)}
                  className="mt-0.5 w-4 h-4 text-blue-600 accent-blue-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{tpl.name}</span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {tpl.numbering === 'none' ? '无编号' : tpl.numbering === 'numeric' ? '数字编号' : '中文数字'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{tpl.description}</p>
                  {/* 预览 */}
                  <pre className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-3 font-mono leading-relaxed overflow-x-auto whitespace-pre">
                    {tpl.preview}
                  </pre>
                </div>
              </label>
            ))
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          {selected && (
            <div className="text-sm text-gray-500">
              已选：<span className="font-medium text-gray-700">{selected.name}</span>
            </div>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              onClick={() => onConfirm(selectedId)}
              disabled={loading || !selectedId}
              className="inline-flex items-center px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              确认导出
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
